import { ServicePageShowImage_productizedService$key } from '#app/__generated__/relay/ServicePageShowImage_productizedService.graphql.js';
import { Avatar } from '#app/components/Avatars/Avatar/Avatar.js';
import { ContraImage } from '#app/components/Avatars/Avatar/types.js';
import { Link } from '#app/components/Link/index.js';
import { Text } from '#app/components/Primitives/Text.js';
import { ServicePageOwnerEmbedModal } from '#app/components/ServicePageOwnerEmbedModal/ServicePageOwnerEmbedModal.js';
import { ServiceRecommendationsView } from '#app/components/ServiceRecommendations/ServiceRecommendationsView.js';
import { ShareButton } from '#app/components/ShareButton/ShareButton.js';
import { Tooltip } from '#app/components/Tooltip/Tooltip.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useIsLargeScreen } from '#app/hooks/useIsLargeScreen.js';
import { useIsSmallScreen } from '#app/hooks/useIsSmallScreen.js';
import {
  Badges,
  BadgesAndName,
  Name,
  ProfileContainer,
} from './ServicePageHeader.styles.js';
import { ServicePageShowImage } from './ServicePageShowImage.js';
import { EditIcon, EmbedIcon } from '@contra/icons/icons';
import { getRoute } from '@contra/routes';
import {
  ComponentProps,
  Fragment,
  ReactNode,
  useCallback,
  useState,
} from 'react';
import { css, styled } from 'styled-components';

const Container = styled.div<{ $tight?: boolean }>`
  ${({ $tight = false, theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: column-reverse;
    gap: 24px;

    ${theme.mediaQueries.xl} {
      position: relative;
      flex-direction: row;
      gap: ${$tight ? '24px' : '32px'};
      height: ${$tight ? '191px' : 'auto'};
    }
  `}
`;

const Details = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 24px;

    ${theme.mediaQueries.xl} {
      flex-grow: 1;
      min-height: 100%;
      justify-content: space-between;
    }
  `}
`;

const MiniButton = styled.div`
  ${({ theme }) => css`
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 175ms ease;

    &:hover {
      background-color: ${theme.colorsV2.gray10};
    }
  `}
`;

const Title = styled(Text)`
  flex-grow: 1;
  font-weight: 600;
`;

const ProfileAndRecommendations = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 24px;

    ${theme.mediaQueries.xl} {
      gap: 16px;
    }
  `}
`;

const RecommendationContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;

    padding: 12px 0;

    border-top: 1px solid ${theme.colorsV2.gray30};
    border-bottom: 1px solid ${theme.colorsV2.gray30};
  `}
`;

const RecommendationIcons = styled.div`
  ${({ theme }) => css`
    padding-right: 8px;
    display: flex;
    gap: 8px;
    color: ${theme.colorsV2.gray80};

    & * {
      cursor: pointer;
    }
  `}
`;

const TitleAndDuration = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  & p {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
  }
`;

const Duration = styled(Text)`
  ${({ theme }) => css`
    color: ${theme.colorsV2.gray80};
  `}
`;

type Props = ComponentProps<typeof ServiceRecommendationsView> & {
  readonly avatarImage: ContraImage | null;
  readonly badges: ReactNode[];
  readonly canEdit: boolean;
  readonly displayUsername: string;
  readonly fullName: string;
  readonly hasCover: boolean;
  readonly onEdit?: VoidFunction;
  readonly productizedServiceRef: ServicePageShowImage_productizedService$key;
  readonly serviceAnalyticsId: number;
  readonly slug: string;
  readonly timeline?: null | string;
  readonly title: string;
  readonly userProfileAnalyticsId: number;
  readonly variant?: 'checkout' | 'default';
};

export const ServicePageHeaderView = ({
  avatarImage,
  badges,
  canEdit,
  displayUsername,
  fullName,
  hasCover,
  onEdit,
  productizedServiceRef,
  serviceAnalyticsId,
  slug,
  timeline,
  title,
  userProfileAnalyticsId,
  variant,
  ...rest
}: Props) => {
  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false);
  const isSmallScreen = useIsSmallScreen();
  const isLargeScreen = !useIsLargeScreen();
  const { track } = useAnalytics();

  const onClickEmbed = useCallback(() => {
    setIsEmbedModalOpen(true);
    track('button', {
      action: 'tapped',
      button_name: 'demand_buy_now_service_embed',
    });
  }, [track]);

  const onClickCloseEmbedModal = () => {
    setIsEmbedModalOpen(false);
  };

  const profileUrl = getRoute(`/@username`, {
    args: {
      username: displayUsername,
    },
  });

  const isCheckout = variant === 'checkout';

  return (
    <>
      <Container $tight={isCheckout}>
        <ServicePageShowImage
          isReadOnly={isCheckout}
          productizedServiceRef={productizedServiceRef}
          width={isCheckout ? 255 : undefined}
        />
        <Details>
          {isCheckout ? (
            <TitleAndDuration>
              <Title textStyle="sixthLevelHeadline">{title}</Title>
              <Duration textStyle="bodySmall">
                Fixed Price{timeline ? ' • ' : null}
                {timeline}
              </Duration>
            </TitleAndDuration>
          ) : (
            <Title
              textStyle={
                isLargeScreen ? 'fourthLevelHeadline' : 'fifthLevelHeadline'
              }
            >
              {title}
            </Title>
          )}

          <ProfileAndRecommendations>
            <Link
              target="_blank"
              to={profileUrl}
            >
              <ProfileContainer>
                <Avatar
                  avatarImage={avatarImage}
                  size="sm"
                />

                <BadgesAndName>
                  <Badges>
                    {badges.map((badge, index) => (
                      <Fragment key={index}>{badge}</Fragment>
                    ))}
                  </Badges>
                  <Name>{fullName}</Name>
                </BadgesAndName>
              </ProfileContainer>
            </Link>
            <RecommendationContainer>
              <ServiceRecommendationsView
                {...rest}
                mode={isSmallScreen ? 'compact' : 'normal'}
              />

              {isCheckout ? null : (
                <RecommendationIcons>
                  {canEdit ? (
                    <Tooltip content="Embed">
                      <MiniButton>
                        <EmbedIcon
                          aria-label="Embed service"
                          onClick={onClickEmbed}
                          role="button"
                          size={16}
                        />
                      </MiniButton>
                    </Tooltip>
                  ) : null}
                  {canEdit ? (
                    <Tooltip content="Edit">
                      <MiniButton>
                        <EditIcon
                          aria-label="Edit service"
                          onClick={onEdit}
                          role="button"
                          size={16}
                        />
                      </MiniButton>
                    </Tooltip>
                  ) : null}
                  <Tooltip content="Share">
                    <MiniButton>
                      <ShareButton
                        analyticsProperties={{
                          is_owner: canEdit,
                          owner_id: userProfileAnalyticsId,
                          service_id: serviceAnalyticsId,
                        }}
                        toastSuccessMessage="The service link was successfully copied to your clipboard."
                        url={`https://contra.com${getRoute('/s/@slug', {
                          args: {
                            slug,
                          },
                        })}`}
                        variant="minimal"
                      />
                    </MiniButton>
                  </Tooltip>
                </RecommendationIcons>
              )}
            </RecommendationContainer>
          </ProfileAndRecommendations>
        </Details>
      </Container>

      <ServicePageOwnerEmbedModal
        hasCover={hasCover}
        isOpen={isEmbedModalOpen}
        onClose={onClickCloseEmbedModal}
      />
    </>
  );
};
